import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FaLock, FaMotorcycle, FaStore } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useStoreStatus } from '../../context/StoreStatusContext';
import { KITCHEN_CLOSED_CHECKOUT_MESSAGE } from '../../utils/storeStatus';
import { apiGetProfile, buildAuthRequiredRedirectUrl, readStoredAuth } from '../Account/profileFormUtils';
import { fetchUserAddresses } from '../Cart/addressesApi';
import { storeSelectedAddressId } from '../Cart/cartDistanceUtils';
import {
  computeOrderGrandTotal,
  isCouponEligible,
  isDeliveryAddressConfirmed,
} from '../Cart/cartCouponUtils';
import { resolveAppliedCoupon } from '../Cart/cartCouponStorage';
import {
  distanceKmFromKitchen,
  findSavedAddressForCheckoutForm,
  resolveCheckoutDistanceKm,
} from '../Cart/checkoutDistanceUtils';
import { useCartCharges } from '../Cart/useCartCharges';
import { findSelectedDeliveryAddress } from '../Account/Addresses/addressManagementUtils';
import CheckoutAddressSection from './CheckoutAddressSection';
import CheckoutContactSection from './CheckoutContactSection';
import CheckoutOrderReviewNotice from './CheckoutOrderReviewNotice';
import {
  resolveInitialSelectedAddressId,
  savedAddressToCheckoutForm,
} from './checkoutAddressUtils';
import {
  contactFromProfilePayload,
  contactFromStoredUser,
  mergeCheckoutContact,
} from './checkoutContactUtils';
import {
  DeferredChargeLabel,
  DeferredChargeValue,
  OrderSummaryDeferredFootnote,
} from '../../components/OrderSummaryDeferredNote/OrderSummaryDeferredNote';
import {
  buildCheckoutPayload,
  hasCheckoutErrors,
  validateCheckoutForPayment,
} from './checkoutValidationUtils';
import { persistCheckoutPayload } from '../Account/Payment/paymentCheckoutState';
import { clearStoredPendingOrderPayment } from '../Account/Payment/pendingOrderPaymentState';
import { sanitizeLocalMobileInput } from '../../utils/phone';
import './Checkout.css';

const CHECKOUT_FORM_ID = 'checkout-main-form';

const Checkout = () => {
  const { cartItems, cartTotal } = useCart();
  const { isKitchenOpen } = useStoreStatus();
  const { state } = useLocation();
  const navigate = useNavigate();

  const restoredCoupon = useMemo(() => resolveAppliedCoupon(state), [state]);
  const appliedCoupon = isCouponEligible(restoredCoupon, cartTotal) ? restoredCoupon : null;

  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    fulfillmentType: state?.fulfillmentType === 'pickup' ? 'pickup' : 'delivery',
    address1: '',
    address2: '',
    city: '',
    state: '',
    pincode: '',
    notes: '',
    deliveryTime: 'asap',
  });

  const [errors, setErrors] = useState({});
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showManualAddress, setShowManualAddress] = useState(false);
  const [addressesReady, setAddressesReady] = useState(false);
  const [contactPrefilled, setContactPrefilled] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [addressSaveNotice, setAddressSaveNotice] = useState('');

  const isSignedIn = Boolean(readStoredAuth().token);

  useEffect(() => {
    const { token, user } = readStoredAuth();
    if (!token) return;

    const stored = contactFromStoredUser(user);
    setForm((prev) => ({
      ...prev,
      fullName: prev.fullName || stored.fullName,
      phone: prev.phone || stored.phone,
      email: prev.email || stored.email,
    }));
    if (stored.fullName || stored.phone) setContactPrefilled(true);

    let cancelled = false;
    (async () => {
      try {
        const { res, data } = await apiGetProfile(token);
        if (cancelled || !res.ok) return;
        const profile = contactFromProfilePayload(data);
        const merged = mergeCheckoutContact({ stored, profile });
        setForm((prev) => ({
          ...prev,
          fullName: prev.fullName || merged.fullName,
          phone: prev.phone || merged.phone,
          email: prev.email || merged.email,
        }));
        if (merged.fullName || merged.phone) setContactPrefilled(true);
      } catch {
        /* keep stored-user prefill */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const reloadAddresses = useCallback(async () => {
    const { token } = readStoredAuth();
    if (!token) {
      setSavedAddresses([]);
      return [];
    }
    setAddressesLoading(true);
    try {
      const list = await fetchUserAddresses(token);
      setSavedAddresses(list);
      return list;
    } catch {
      setSavedAddresses([]);
      return [];
    } finally {
      setAddressesLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { token } = readStoredAuth();
      if (!token) {
        if (!cancelled) {
          setShowManualAddress(true);
          setAddressesReady(true);
        }
        return;
      }
      const list = await reloadAddresses();
      if (cancelled) return;
      setAddressesReady(true);
      if (form.fulfillmentType !== 'delivery') return;
      const initialId = resolveInitialSelectedAddressId(list);
      if (initialId != null) {
        setSelectedAddressId(initialId);
        setShowManualAddress(false);
        storeSelectedAddressId(initialId);
        const row = findSelectedDeliveryAddress(list, initialId);
        if (row) {
          setForm((prev) => ({ ...prev, ...savedAddressToCheckoutForm(row) }));
        }
      } else if (list.length === 0) {
        setShowManualAddress(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, [reloadAddresses]);

  const selectedDeliveryAddress = useMemo(
    () =>
      form.fulfillmentType === 'delivery'
        ? findSelectedDeliveryAddress(savedAddresses, selectedAddressId)
        : null,
    [form.fulfillmentType, savedAddresses, selectedAddressId],
  );

  const addressConfirmed = isDeliveryAddressConfirmed({
    address1: form.address1,
    city: form.city,
    pincode: form.pincode,
  });

  const matchedAddress = useMemo(
    () =>
      form.fulfillmentType === 'delivery' && showManualAddress && addressConfirmed
        ? findSavedAddressForCheckoutForm(savedAddresses, form)
        : null,
    [savedAddresses, form, addressConfirmed, showManualAddress],
  );

  const deliveryPricingReady =
    form.fulfillmentType !== 'delivery' ||
    selectedDeliveryAddress != null ||
    (showManualAddress && matchedAddress != null);

  const checkoutDistanceKm = resolveCheckoutDistanceKm(
    form.fulfillmentType,
    deliveryPricingReady,
    selectedDeliveryAddress
      ? distanceKmFromKitchen(selectedDeliveryAddress)
      : matchedAddress
        ? distanceKmFromKitchen(matchedAddress)
        : state?.distanceKm,
  );

  const applySelectedAddress = (id, list = savedAddresses, fallbackRow = null) => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) return;

    let row = findSelectedDeliveryAddress(list, numericId);
    if (
      !row &&
      fallbackRow &&
      Number(fallbackRow.id) === numericId &&
      Number.isFinite(fallbackRow.latitude) &&
      Number.isFinite(fallbackRow.longitude)
    ) {
      row = fallbackRow;
    }
    if (!row) return;

    setSelectedAddressId(numericId);
    setShowManualAddress(false);
    storeSelectedAddressId(numericId);
    setForm((prev) => ({ ...prev, ...savedAddressToCheckoutForm(row) }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.address1;
      delete next.city;
      delete next.pincode;
      delete next.addressPick;
      return next;
    });
  };

  const handleAddressSaved = async (address) => {
    const list = await reloadAddresses();
    const id = address?.id != null ? Number(address.id) : null;
    if (id == null) return;

    const mergedList =
      list.some((a) => Number(a.id) === id) || !address
        ? list
        : [...list, address];

    if (mergedList !== list) {
      setSavedAddresses(mergedList);
    }

    applySelectedAddress(id, mergedList, address);
    setAddressSaveNotice(
      'Address saved and selected. Review your order, then tap Proceed to Payment when you are ready.',
    );
  };

  const charges = useCartCharges({
    cartItems,
    cartTotal,
    fulfillmentType: form.fulfillmentType,
    appliedCoupon,
    distanceKm: checkoutDistanceKm,
  });

  const {
    packagingFee,
    delivery,
    gstTax,
    gstPercent,
    discount,
    deferDeliveryAndTax,
    status: chargesStatus,
    error: chargesError,
  } = charges;

  const grandTotal = computeOrderGrandTotal({
    cartTotal,
    discount,
    packagingFee,
    delivery,
    gstTax,
    deferDeliveryAndTax,
  });

  const handleChange = (e) => {
    setForm((prev) => {
      const name = e.target.name;
      let value = e.target.value;
      if (name === 'phone') {
        value = sanitizeLocalMobileInput(value, '91');
      }
      return { ...prev, [name]: value };
    });
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
    if (e.target.name === 'notes' && e.target.value.trim()) {
      setNotesExpanded(true);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
  };

  const handlePayClick = () => {
    if (!isKitchenOpen) return;
    if (!readStoredAuth().token) {
      navigate(buildAuthRequiredRedirectUrl('/checkout'));
      return;
    }

    const nextErrors = validateCheckoutForPayment({
      form,
      selectedAddressId,
      deliveryPricingReady,
      deferDeliveryAndTax,
      chargesLoading: chargesStatus === 'loading',
      cartEmpty: cartItems.length === 0,
    });
    if (hasCheckoutErrors(nextErrors)) {
      setErrors(nextErrors);
      if (nextErrors._general) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    const checkoutPayload = buildCheckoutPayload({
      form,
      selectedAddressId,
      appliedCoupon,
      cartItems,
      cartTotal,
      discount,
      packagingFee,
      delivery,
      gstTax,
      gstPercent: gstPercent ?? 5,
      grandTotal,
      distanceKm: checkoutDistanceKm ?? 0,
    });

    clearStoredPendingOrderPayment();
    persistCheckoutPayload(checkoutPayload);
    navigate('/payment', { state: { checkoutPayload } });
  };

  const payDisabled = chargesStatus === 'loading' || deferDeliveryAndTax || !isKitchenOpen;

  const checkoutPayButton = (
    <>
      {!isKitchenOpen ? (
        <p className="checkout-kitchen-closed" role="status">
          {KITCHEN_CLOSED_CHECKOUT_MESSAGE}{' '}
          <Link to="/subscription-cart">Continue with a subscription</Link>.
        </p>
      ) : null}
      <button
        type="button"
        className={`checkout-pay-btn${!isKitchenOpen ? ' checkout-pay-btn--kitchen-closed' : ''}`}
        disabled={payDisabled}
        onClick={handlePayClick}
      >
        <FaLock /> PROCEED TO PAYMENT &nbsp;—&nbsp; ₹{grandTotal.toFixed(2)}
      </button>
    </>
  );

  if (cartItems.length === 0) {
    return (
      <div className="checkout-page">
        <div className="page-header">
          <div className="page-header-content">
            <div className="container">
              <h1>CHECKOUT</h1>
            </div>
          </div>
        </div>
        <section className="checkout-empty">
          <div className="container">
            <p>
              Nothing to checkout. <Link to="/our-menu">Browse menu</Link>
            </p>
          </div>
        </section>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to={buildAuthRequiredRedirectUrl('/checkout')} replace />;
  }

  return (
    <div className="checkout-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="container">
            <h1>CHECKOUT</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/cart">Cart</Link>
              <span>/</span>
              <span>Checkout</span>
            </div>
          </div>
        </div>
      </div>

      <section className="checkout-section">
        <div className="container">
          <div className="checkout-layout">
            <div className="checkout-form-wrap">
              <div className="checkout-fulfillment">
                <p className="checkout-fulfillment__label">Choose how you want your order</p>
                <div className="checkout-fulfillment__options">
                  <button
                    type="button"
                    className={`cf-fulfillment-btn${form.fulfillmentType === 'delivery' ? ' cf-fulfillment-btn--active' : ''}`}
                    onClick={() => {
                      setForm((prev) => ({ ...prev, fulfillmentType: 'delivery' }));
                      if (addressesReady && savedAddresses.length > 0) {
                        const id = resolveInitialSelectedAddressId(savedAddresses);
                        if (id != null) applySelectedAddress(id);
                      }
                    }}
                  >
                    <span className="cf-fulfillment-btn__title">
                      <FaMotorcycle /> Delivery
                    </span>
                    <span className="cf-fulfillment-btn__meta">Delivered to your address</span>
                  </button>
                  <button
                    type="button"
                    className={`cf-fulfillment-btn${form.fulfillmentType === 'pickup' ? ' cf-fulfillment-btn--active' : ''}`}
                    onClick={() => {
                      setForm((prev) => ({ ...prev, fulfillmentType: 'pickup' }));
                      setSelectedAddressId(null);
                    }}
                  >
                    <span className="cf-fulfillment-btn__title">
                      <FaStore /> Pickup
                    </span>
                    <span className="cf-fulfillment-btn__meta">
                      Collect from store, no delivery charge
                    </span>
                  </button>
                </div>
              </div>

              {errors._general ? (
                <p className="cf-error checkout-general-error" role="alert">
                  {errors._general}
                </p>
              ) : null}

              <form
                id={CHECKOUT_FORM_ID}
                className="checkout-form"
                onSubmit={handleFormSubmit}
                noValidate
              >
                <CheckoutContactSection
                  fullName={form.fullName}
                  phone={form.phone}
                  email={form.email}
                  errors={errors}
                  isSignedIn={isSignedIn}
                  contactPrefilled={contactPrefilled}
                  onChange={handleChange}
                />

                {form.fulfillmentType === 'delivery' ? (
                  <>
                    <CheckoutAddressSection
                      isSignedIn={isSignedIn}
                      loading={addressesLoading}
                      addresses={savedAddresses}
                      selectedId={selectedAddressId}
                      onSelect={(id) => {
                        setAddressSaveNotice('');
                        applySelectedAddress(id);
                      }}
                      onAddressSaved={handleAddressSaved}
                    />
                    {addressSaveNotice ? (
                      <p className="checkout-address-save-notice" role="status">
                        {addressSaveNotice}
                      </p>
                    ) : null}
                    {errors.addressPick ? (
                      <p className="cf-error checkout-address-pick-error">{errors.addressPick}</p>
                    ) : null}

                    {(showManualAddress || !isSignedIn) && (
                      <>
                        <div className="cf-group">
                          <label>Address Line 1 *</label>
                          <input
                            type="text"
                            name="address1"
                            placeholder="House / Flat No., Street"
                            value={form.address1}
                            onChange={handleChange}
                            className={errors.address1 ? 'cf-input--error' : ''}
                          />
                          {errors.address1 && <span className="cf-error">{errors.address1}</span>}
                        </div>

                        <div className="cf-group">
                          <label>Address Line 2 (optional)</label>
                          <input
                            type="text"
                            name="address2"
                            placeholder="Area, Landmark"
                            value={form.address2}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="cf-row">
                          <div className="cf-group">
                            <label>City *</label>
                            <input
                              type="text"
                              name="city"
                              placeholder="City"
                              value={form.city}
                              onChange={handleChange}
                              className={errors.city ? 'cf-input--error' : ''}
                            />
                            {errors.city && <span className="cf-error">{errors.city}</span>}
                          </div>
                          <div className="cf-group">
                            <label>State</label>
                            <select name="state" value={form.state} onChange={handleChange}>
                              <option value="">Select state</option>
                              <option value="Telangana">Telangana</option>
                              <option value="Andhra Pradesh">Andhra Pradesh</option>
                              <option value="Karnataka">Karnataka</option>
                              <option value="Maharashtra">Maharashtra</option>
                              <option value="Tamil Nadu">Tamil Nadu</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                        </div>

                        <div className="cf-group">
                          <label>Pincode *</label>
                          <input
                            type="text"
                            name="pincode"
                            placeholder="6-digit pincode"
                            value={form.pincode}
                            onChange={handleChange}
                            maxLength={6}
                            className={errors.pincode ? 'cf-input--error' : ''}
                          />
                          {errors.pincode && <span className="cf-error">{errors.pincode}</span>}
                        </div>
                      </>
                    )}

                  </>
                ) : (
                  <>
                    <div className="checkout-pickup-note">
                    <p>
                      <strong>Pickup selected:</strong> No delivery charges will be applied.
                    </p>
                    <p>Pickup point: DigiRasa Kitchen Hub, Miyapur, Hyderabad.</p>
                    <p>Carry your order confirmation and mobile number while collecting.</p>
                    </div>
                  </>
                )}

                {chargesError && (
                  <p className="checkout-charges-alert" role="alert">
                    {chargesError}
                  </p>
                )}

                <div className="checkout-notes">
                  {!notesExpanded && !form.notes.trim() ? (
                    <button
                      type="button"
                      className="checkout-notes__toggle"
                      onClick={() => setNotesExpanded(true)}
                    >
                      + Add special instructions (optional)
                    </button>
                  ) : (
                    <div className="cf-group">
                      <label htmlFor="checkout-notes">Special instructions (optional)</label>
                      <textarea
                        id="checkout-notes"
                        name="notes"
                        placeholder="Dietary preferences or instructions for delivery…"
                        value={form.notes}
                        onChange={handleChange}
                        rows={2}
                      />
                    </div>
                  )}
                </div>

                <CheckoutOrderReviewNotice
                  fulfillmentType={form.fulfillmentType === 'pickup' ? 'pickup' : 'delivery'}
                />
              </form>
            </div>

            <aside className="checkout-summary">
              <h2 className="checkout-section-title">Order Summary</h2>
              <div className="checkout-order-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="checkout-order-item">
                    <div className="co-item__img">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div className="co-item__info">
                      <p className="co-item__name">{item.name}</p>
                      <p className="co-item__meta">
                        Qty {item.quantity}
                        {item.category ? ` · ${item.category}` : ''}
                      </p>
                    </div>
                    <span className="co-item__total">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="checkout-price-rows">
                <div className="cpr cpr--fulfillment">
                  <span>Order Type</span>
                  <span className="cpr-order-type">
                    {form.fulfillmentType === 'pickup' ? <FaStore /> : <FaMotorcycle />}
                    {form.fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'}
                  </span>
                </div>
                <div className="cpr">
                  <span>Subtotal</span>
                  <span>₹{cartTotal.toFixed(2)}</span>
                </div>
                {discount > 0 && appliedCoupon && (
                  <div className="cpr cpr--discount">
                    <span>Discount ({appliedCoupon.code})</span>
                    <span>−₹{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="cpr">
                  <span>Packaging fee</span>
                  <span>
                    {chargesStatus === 'loading' ? '…' : `₹${packagingFee.toFixed(2)}`}
                  </span>
                </div>
                {form.fulfillmentType !== 'pickup' ? (
                  <div className={`cpr${deferDeliveryAndTax ? ' cpr--pending' : ''}`}>
                    <span>
                      {deferDeliveryAndTax ? (
                        <DeferredChargeLabel>Delivery</DeferredChargeLabel>
                      ) : (
                        'Delivery'
                      )}
                    </span>
                    <span>
                      {deferDeliveryAndTax ? (
                        <DeferredChargeValue />
                      ) : chargesStatus === 'loading' ? (
                        '…'
                      ) : delivery === 0 ? (
                        <span className="cpr--free">FREE</span>
                      ) : (
                        `₹${delivery.toFixed(2)}`
                      )}
                    </span>
                  </div>
                ) : null}
                <div className={`cpr${deferDeliveryAndTax ? ' cpr--pending' : ''}`}>
                  <span>
                    {deferDeliveryAndTax ? (
                      <DeferredChargeLabel>GST / Tax</DeferredChargeLabel>
                    ) : (
                      'GST / Tax'
                    )}
                  </span>
                  <span>
                    {deferDeliveryAndTax ? (
                      <DeferredChargeValue />
                    ) : chargesStatus === 'loading' ? (
                      '…'
                    ) : (
                      `₹${gstTax.toFixed(2)}`
                    )}
                  </span>
                </div>
                {deferDeliveryAndTax && (
                  <OrderSummaryDeferredFootnote>
                    {savedAddresses.length === 0
                      ? 'Add a delivery address in My Addresses (with map location), or pick up in store, to calculate delivery and GST.'
                      : 'Enter a delivery address that matches a saved address in My Addresses (with map location), or pick up in store, to calculate delivery and GST.'}
                  </OrderSummaryDeferredFootnote>
                )}
                <div className="cpr cpr--total">
                  <span>{deferDeliveryAndTax ? 'To pay (estimated)' : 'To pay'}</span>
                  <span>
                    {chargesStatus === 'loading' ? '…' : `₹${grandTotal.toFixed(2)}`}
                  </span>
                </div>
              </div>

              <div className="checkout-summary-actions">
                {checkoutPayButton}
                <Link to="/cart" className="checkout-back-btn checkout-back-btn--summary">
                  ← Back to Cart
                </Link>
                <p className="checkout-security-note">
                  🔒 Your payment is secured and encrypted. We never store card details.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <div className="checkout-mobile-bar">
        <div className="checkout-mobile-bar__total">
          <span>To pay</span>
          <strong>{chargesStatus === 'loading' ? '…' : `₹${grandTotal.toFixed(2)}`}</strong>
        </div>
        <button
          type="button"
          className={`checkout-mobile-bar__pay checkout-pay-btn${!isKitchenOpen ? ' checkout-pay-btn--kitchen-closed' : ''}`}
          disabled={payDisabled}
          onClick={handlePayClick}
        >
          <FaLock aria-hidden /> Pay
        </button>
      </div>
    </div>
  );
};

export default Checkout;
